<template>
    <div class="main-content">
        <!-- Sistema de Notificações (Toasts)[cite: 3, 4] -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <!-- Header e Breadcrumb[cite: 3, 4] -->
        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Empresas</li>
                </ol>
            </nav>
        </header>

        <!-- Card Principal[cite: 3, 4] -->
        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Empresas</h4>
                    <span class="text-muted fs-7">Exibindo {{ branches.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3"
                                @click="confirmDelete(selectedIds.length, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{ selectedIds.length }})
                            </button>
                        </div>
                    </transition>
                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <button class="btn btn-pmg-primary" @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Nova Empresa
                    </button>
                </div>
            </div>

            <!-- Seção de Filtro[cite: 3, 4] -->
            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-10">
                            <input v-model="filter.name" type="text" class="form-control form-control-pmg"
                                placeholder="Filtrar por nome da empresa...">
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabela: Mantendo apenas a classe nativa sem overrides de CSS[cite: 4] -->
            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 35%">Nome</th>
                            <th style="width: 25%">CNPJ</th>
                            <th style="width: 20%">Status</th>
                            <th style="width: 15%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="branches.length > 0">
                        <tr v-for="item in branches" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <td><input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <td class="fw-bold text-dark">{{ item.name }}</td>
                            <td class="text-muted">{{ item.cnpj || '---' }}</td>
                            <td>
                                <span class="small fw-bold" :class="item.is_active ? 'text-success' : 'text-danger'">
                                    {{ item.is_active ? '● Ativa' : '● Inativa' }}
                                </span>
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0">
                                        <li><a class="dropdown-item" href="#"
                                                @click.prevent="openModal('edit', item)"><i
                                                    class="bi bi-pencil me-2"></i>Editar</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li>
                                            <a class="dropdown-item text-danger" href="#"
                                                @click.prevent="confirmDelete(item, false)">
                                                <i class="bi bi-trash me-2"></i>Excluir
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="5" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-inbox fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Não há registros no momento.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Paginação[cite: 3, 4] -->
            <div class="text-center mt-4" v-if="hasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMore" :disabled="loading"
                    style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <span>{{ loading ? 'Carregando...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <!-- Modais e Scripts mantidos conforme lógica anterior[cite: 3, 5] -->
        <div class="modal fade" id="modalBranch" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4">
                        <h5 class="fw-bold m-0">{{ isEditing ? 'Editar Empresa' : 'Nova Empresa' }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body px-4">
                        <div class="mb-3">
                            <label class="form-label small fw-bold text-muted">Nome da Empresa</label>
                            <input v-model="form.name" type="text" class="form-control form-control-pmg"
                                placeholder="Nome da unidade">
                        </div>
                        <div class="mb-3">
                            <label class="form-label small fw-bold text-muted">CNPJ</label>
                            <input type="text" class="form-control form-control-pmg" placeholder="00.000.000/0000-00"
                                :value="form.cnpj" @input="handleCNPJInput" maxlength="18">
                        </div>
                        <div class="form-check form-switch mb-3">
                            <input v-model="form.is_active" class="form-check-input" type="checkbox" id="branchStatus">
                            <label class="form-check-label small fw-bold text-muted" for="branchStatus">Empresa
                                Ativa</label>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isFormValid" @click="saveBranch">
                            {{ isEditing ? 'Atualizar' : 'Salvar Empresa' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3"><i class="bi bi-exclamation-triangle-fill fs-1"></i></div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">
                            {{ isBulkDelete ? `Excluir ${selectedIds.length} empresa(s) selecionada(s)?` : `Deseja
                            excluir a
                            empresa "${itemToDelete?.name || itemToDelete?.name || 'selecionada'}"?` }}
                        </p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    data() {
        return {
            branches: [],
            alerts: [],
            isEditing: false,
            form: { id: null, name: '', cnpj: '', is_active: true },
            selectedIds: [],
            filter: { name: '' },
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,
            showFilter: false,
            modalInstance: null,
            itemToDelete: null
        }
    },
    computed: {
        isAllSelected() { return this.branches.length > 0 && this.branches.every(i => this.selectedIds.includes(i.id)); },
        isFormValid() { return this.form.name.trim(); }
    },
    mounted() { this.fetchBranches(); },
    methods: {
        async fetchBranches(page = 1, append = false) {
            this.loading = true;
            try {
                const response = await api.get('/branches', { params: { page, search: this.filter.name } });
                const { data, meta } = response.data;
                this.branches = append ? [...this.branches, ...data] : data;
                this.totalItems = meta.total;
                this.currentPage = meta.current_page;
                this.lastPage = meta.last_page;
                this.hasMore = this.currentPage < this.lastPage;
            } catch (error) {
                this.showAlert('Falha ao carregar dados.', 'error');
            } finally { this.loading = false; }
        },
        handleCNPJInput(e) {
            let value = e.target.value.replace(/\D/g, '');

            if (value.length > 14) {
                value = value.substring(0, 14);
            }

            value = value.replace(/^(\d{2})(\d)/, '$1.$2');
            value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
            value = value.replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4');
            value = value.replace(/(\d{4})(\d)/, '$1-$2');

            this.form.cnpj = value;
        },
        openModal(mode, item = null) {
            this.isEditing = mode === 'edit';
            this.form = mode === 'edit' ? { ...item } : { id: null, name: '', cnpj: '', is_active: true };
            const el = document.getElementById('modalBranch');
            this.modalInstance = window.bootstrap.Modal.getOrCreateInstance(el);
            this.modalInstance.show();
        },
        async saveBranch() {
            this.loading = true;
            try {
                const payload = { ...this.form, cnpj: this.form.cnpj ? this.form.cnpj.replace(/\D/g, '') : null };
                if (this.isEditing) await api.put(`/branches/${this.form.id}`, payload);
                else await api.post('/branches', payload);
                this.showAlert("Sucesso!", "success");
                this.modalInstance.hide();
                this.fetchBranches(1, false);
            } catch (e) {
                if (e.response?.status === 403) {
                    this.showAlert("Permissão de acesso ao recurso não autorizada.", "error");
                }else if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao salvar.", "error");
                }

            } finally { this.loading = false; }
        },
        confirmDelete(target, isBulk = false) {
            this.itemToDelete = target; // Pode ser o objeto da empresa ou um número
            this.isBulkDelete = isBulk;

            // Instanciação correta seguindo o seu padrão
            const el = document.getElementById('confirmDeleteModal');
            const modal = window.bootstrap.Modal.getOrCreateInstance(el);
            modal.show();
        },
        async executeDeletion() {
            try {
                await api.delete(`/branches/${this.itemToDelete.id}`);
                this.showAlert("Removido.", "danger");
                this.fetchBranches(1, false);
            } catch (e) { 
                if (e.response?.status === 403) {
                    this.showAlert("Permissão de acesso ao recurso não autorizada.", "error");
                }else if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao excluir.", "error");
                }
            } finally { 
                window.bootstrap.Modal.getInstance(document.getElementById('confirmDeleteModal')).hide(); 
            }
        },
        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = type === 'danger' ? 'bi-exclamation-circle-fill' : (type === 'error' ? 'bi-x-circle-fill' : 'bi-check-circle-fill');
            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), 8000);
        },
        removeAlert(id) { this.alerts = this.alerts.filter(a => a.id !== id); },
        loadMore() { if (this.hasMore) this.fetchBranches(this.currentPage + 1, true); },
        applyFilter() { this.showFilter = false; this.fetchBranches(1, false); },
        toggleAll(e) { this.selectedIds = e.target.checked ? this.branches.map(b => b.id) : []; }
    }
}
</script>

<style scoped>
/* ESTILOS REPRODUZINDO FIELMENTE O COMPONENTE DE REFERÊNCIA[cite: 4] */
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

/* Tabela: Removido qualquer override de overflow-y[cite: 4] */
.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.table-responsive {
    overflow: visible !important;
    /* Isso mata o scroll e permite que o menu flutue */
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
    border: 0;
    /* Certifique-se de que NÃO existe overflow: hidden aqui */
}

/* Ajuste no Dropdown para ele não herdar restrições da linha */
.dropdown-menu {
    position: absolute;
    z-index: 1060;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>